<script setup>
// §8.5 SearchField — the free-text filter above a list or table.
//
// A NATIVE <input type="search"> so the platform's own affordances (Esc to
// clear, the browser's search history) come for free; the leading icon and the
// clear button are ours because the native ones can't be themed consistently.
//
// Filtering is live: there is no submit. A filter box that needs Enter reads as
// a search that goes somewhere, and this one doesn't — it narrows what is
// already on screen.
import LField from './Field.vue'
import LIcon from '../base/Icon.vue'

defineProps({
	label: { type: String, default: null },
	hint: { type: String, default: null },
	placeholder: { type: String, default: 'Filter…' },
	// The accessible name when there is no visible label.
	ariaLabel: { type: String, default: null },
	// Accessible name for the clear button, which has no visible text.
	clearLabel: { type: String, default: 'Clear the filter' },
})
const model = defineModel({ default: '' })
</script>

<template>
	<LField :label="label" :hint="hint" v-slot="{ id }">
		<div class="search-wrap">
			<span class="lead">
				<LIcon name="magnifying-glass" :size="13" />
			</span>
			<input :id="id" v-model="model" type="search" :placeholder="placeholder" :aria-label="ariaLabel" class="search-input" />
			<button v-if="model" type="button" class="clear" :aria-label="clearLabel" :title="clearLabel" @click="model = ''">
				<LIcon name="xmark" :size="13" />
			</button>
		</div>
	</LField>
</template>

<style scoped>
.search-wrap
{
	position: relative;
	display: flex;
	align-items: center;
}

.search-input
{
	font-family: var(--font-ui);
	font-size: var(--text-base);
	color: var(--text);
	padding: 9px 36px 9px 34px;
	border: 1px solid var(--line);
	border-radius: var(--r);
	background: var(--surface);
	min-height: var(--target-min);
	width: 100%;
}

.search-input::placeholder
{
	color: var(--text-3);
}

.search-input:hover
{
	border-color: var(--line-strong);
}

.search-input:focus-visible
{
	border-color: var(--interactive);
}

/* The platform's own clear affordance is replaced by the button below, which we
   can size to the 24px minimum target. */
.search-input::-webkit-search-cancel-button
{
	appearance: none;
}

.lead
{
	position: absolute;
	left: 12px;
	color: var(--icon);
	pointer-events: none;
}

.clear
{
	position: absolute;
	right: 6px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: var(--target-min);
	height: var(--target-min);
	color: var(--icon);
	border-radius: var(--r-sm);
	border: 0;
	background: none;
	cursor: pointer;
}

.clear:hover
{
	background: var(--surface-sunken);
	color: var(--text);
}
</style>
